import { useState } from 'react';
import { X, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import { submitActivityFeedback, uploadUserImage } from '../api/api';

const FeedbackForm = ({ activityId, feedbackStatus }) => {
  const [experienceText, setExperienceText] = useState('');
  const [feedbackImages, setFeedbackImages] = useState([]);
  const [feedbackPreviews, setFeedbackPreviews] = useState([]);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(feedbackStatus === 'submitted');
  const [feedbackError, setFeedbackError] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const handleSubmit = async () => {
    setSubmittingFeedback(true);
    setFeedbackError('');
    try {
      const uploadedUrls = [];
      for (const file of feedbackImages) {
        const res = await uploadUserImage(file);
        uploadedUrls.push(res.data.url);
      }
      await submitActivityFeedback(activityId, { review_text: experienceText.trim(), media_urls: uploadedUrls });
      setFeedbackSubmitted(true);
      feedbackPreviews.forEach((url) => URL.revokeObjectURL(url));
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to submit. Please try again.';
      if (e?.response?.status === 409) setFeedbackSubmitted(true);
      else setFeedbackError(msg);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-800">Share your experience</p>
        <button
          onClick={() => setCollapsed((p) => !p)}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid={`share-collapse-toggle-${activityId}`}
          aria-label={collapsed ? 'Expand share experience' : 'Collapse share experience'}
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
      </div>
      {!collapsed && (
        feedbackSubmitted ? (
          <div className="py-2 text-center">
            <p className="text-sm font-semibold text-green-700">Thank you! Your experience was submitted.</p>
            <p className="text-xs text-gray-400 mt-1">Pending moderation.</p>
          </div>
        ) : (
          <>
            <textarea
              value={experienceText}
              onChange={(e) => setExperienceText(e.target.value)}
              placeholder="Tell us about your experience..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-green-500 bg-white"
              data-testid={`experience-text-${activityId}`}
            />
            {feedbackPreviews.length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto scrollbar-hide">
                {feedbackPreviews.map((url, j) => (
                  <div key={j} className="relative flex-shrink-0">
                    <img loading="lazy" src={url} alt="" className="w-14 h-14 object-cover rounded-lg" />
                    <button
                      onClick={() => {
                        URL.revokeObjectURL(feedbackPreviews[j]);
                        setFeedbackImages((p) => p.filter((_, k) => k !== j));
                        setFeedbackPreviews((p) => p.filter((_, k) => k !== j));
                      }}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              {feedbackPreviews.length < 5 && (
                <label className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors bg-white">
                  <Upload size={13} />
                  Photo ({feedbackPreviews.length}/5)
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const remaining = 5 - feedbackImages.length;
                      const toAdd = files.slice(0, remaining).filter((f) => f.size <= 5 * 1024 * 1024);
                      setFeedbackImages((p) => [...p, ...toAdd]);
                      setFeedbackPreviews((p) => [...p, ...toAdd.map((f) => URL.createObjectURL(f))]);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
              {feedbackError && <p className="text-xs text-red-500 flex-1">{feedbackError}</p>}
              <button
                disabled={!experienceText.trim() || submittingFeedback}
                onClick={handleSubmit}
                className="ml-auto px-4 py-2 bg-green-600 text-white text-xs font-medium rounded-lg disabled:opacity-40 transition-opacity"
                data-testid={`experience-submit-${activityId}`}
              >
                {submittingFeedback ? 'Sharing\u2026' : 'Share'}
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default FeedbackForm;
